import React from 'react';
import { Button, Modal, Form, TextArea } from 'semantic-ui-react';
import moment from 'moment';
import { apiGet, apiPost } from '../services/apiClient';
import { toast } from 'react-toastify';
import StarRating, { PRIMARY_BLUE } from '../components/CarrierReview/StarRating';
import CarrierReviewParcelSummary from '../components/CarrierReview/CarrierReviewParcelSummary';
import '../components/CarrierReview/carrierReview.scss';
import '../components/styles.scss';

const COMMENT_MAX_LENGTH = 2200;

export default class CarrierReviewModal extends React.Component {
    constructor(args) {
        super(args);

        this._isMounted = false;
        this.state = {
            saving: false,
            loadingParcel: false,
            errors: {},
            rating: args.initialRating || 0,
            comment: args.initialComment || '',
            parcel: args.parcel || null
        };
    }

    componentDidMount() {
        this._isMounted = true;
        if (this.props.open) {
            this.beginOpen();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.beginOpen();
        } else if (this.props.open
            && prevProps.parcelId !== this.props.parcelId
            && this.props.parcelId) {
            this.loadParcel(true);
        } else if (this.props.open
            && prevProps.parcel !== this.props.parcel
            && this.props.parcel) {
            this.setState({ parcel: this.props.parcel, loadingParcel: false });
        }
    }

    beginOpen = () => {
        const { parcel, parcelId } = this.props;
        const needsLoad = !parcel && !!parcelId;

        this.setState({
            saving: false,
            errors: {},
            rating: this.props.initialRating || 0,
            comment: this.props.initialComment || '',
            parcel: parcel || null,
            loadingParcel: needsLoad
        });

        if (needsLoad) {
            this.loadParcel(true);
        }
    };

    loadParcel = (force = false) => {
        const { parcelId, parcel, onClose } = this.props;
        if (!parcelId) {
            return;
        }

        if (!force && parcel) {
            this.setState({ parcel, loadingParcel: false });
            return;
        }

        this.setState({ loadingParcel: true });
        apiGet(`parcels/${parcelId}`)
            .then((data) => {
                if (!this._isMounted) {
                    return;
                }
                this.setState({
                    loadingParcel: false,
                    parcel: data || null
                });
            })
            .catch((err) => {
                if (!this._isMounted) {
                    return;
                }
                this.setState({ loadingParcel: false, parcel: null });
                const errorText = (err && err.error) || 'Не удалось загрузить данные посылки';
                toast.error(errorText);
                if (onClose) {
                    onClose();
                }
            });
    };

    handleSave = () => {
        const { parcelId, onSaved, onClose } = this.props;
        const { rating, comment } = this.state;
        const errors = {};

        if (!rating || rating < 1 || rating > 5) {
            errors.rating = 'Пожалуйста поставьте оценку нажав на соответствующую звезду.';
        }

        if (comment && comment.length > COMMENT_MAX_LENGTH) {
            errors.comment = 'Максимум 2200 символов';
        }

        if (Object.keys(errors).length) {
            this.setState({ errors });
            return;
        }

        this.setState({ saving: true, errors: {} });
        apiPost('carriers/reviews', {
            parcelId: Number(parcelId),
            rating: Number(rating),
            comment: comment || null
        })
            .then((data) => {
                if (!this._isMounted) {
                    return;
                }
                this.setState({ saving: false });
                toast.success('Спасибо за вашу оценку!');
                if (onSaved) {
                    onSaved(data);
                }
                if (onClose) {
                    onClose();
                }
            })
            .catch((err) => {
                if (!this._isMounted) {
                    return;
                }
                const messages = (err && err.messages) || {};
                const errorText = (err && err.error)
                    || messages.rating
                    || messages.comment
                    || messages.parcelId
                    || 'Не удалось сохранить оценку';
                this.setState({
                    saving: false,
                    errors: Object.keys(messages).length ? messages : { form: errorText }
                });
                toast.error(errorText);
            });
    };

    renderAuthorInfo() {
        const { reviewId, authorLogin, authorCreatedAt } = this.props;
        if (!reviewId || !authorCreatedAt) {
            return null;
        }

        const dateText = moment(authorCreatedAt).format('DD.MM.YYYY HH:mm');
        const authorText = authorLogin ? `${dateText}, ${authorLogin}` : dateText;
        return (
            <div className="carrier-review-modal__author">
                {authorText}
            </div>
        );
    }

    render() {
        const { open, onClose } = this.props;
        const { saving, loadingParcel, rating, comment, errors, parcel } = this.state;
        const isVisible = !!open && !loadingParcel && !!parcel;

        return (
            <Modal
                className="modal-confirm carrier-review-modal"
                open={isVisible}
                onClose={onClose}
                size="large"
                closeIcon
            >
                <Modal.Header>Оценка качества работы перевозчика по посылке</Modal.Header>
                <Modal.Content className="modal__content">
                    <CarrierReviewParcelSummary parcel={parcel} />
                    <Form>
                        <Form.Field error={!!errors.rating} required>
                            <label>Как бы вы оценили результат выполненной доставки?</label>
                            <div className="carrier-review-modal__stars">
                                <StarRating
                                    rating={rating}
                                    readOnly={saving}
                                    onChange={(value) => this.setState({ rating: value, errors: {} })}
                                    starRatedColor={PRIMARY_BLUE}
                                    starHoverColor={PRIMARY_BLUE}
                                    starDimension="28px"
                                    starSpacing="4px"
                                    name="carrier-review-modal-rating"
                                />
                            </div>
                            {errors.rating && <div className="ui pointing prompt label">{errors.rating}</div>}
                        </Form.Field>
                        <Form.Field error={!!errors.comment}>
                            <label>Опишите Ваши впечатления или конкретный проблему, возникшую при доставке этой посылки</label>
                            <TextArea
                                rows={8}
                                value={comment}
                                disabled={saving}
                                maxLength={COMMENT_MAX_LENGTH}
                                placeholder="Максимум 2200 символов"
                                onChange={(e, { value }) => this.setState({
                                    comment: value,
                                    errors: { ...this.state.errors, comment: undefined }
                                })}
                            />
                            {this.renderAuthorInfo()}
                            {errors.comment && <div className="ui pointing prompt label">{errors.comment}</div>}
                        </Form.Field>
                        {errors.form && <div className="ui negative message">{errors.form}</div>}
                    </Form>
                </Modal.Content>
                <Modal.Actions className="modal__actions">
                    <Button onClick={onClose} disabled={saving}>Отмена</Button>
                    <Button
                        className="modal__actions_yes"
                        onClick={this.handleSave}
                        loading={saving}
                        disabled={saving || !rating}
                    >
                        Сохранить
                    </Button>
                </Modal.Actions>
            </Modal>
        );
    }
}



// WEBPACK FOOTER //
// ./App/modals/carrierReviewModal.jsx